import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  FileText, 
  PieChart, 
  BarChart3, 
  Download, 
  Calendar,
  Filter,
  Eye,
  Settings,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';

// API Service
import { financialReportsAPI, accountingAPI } from '../../services/api';

// Components
import ProfitLossReport from './ProfitLossReport';
import BalanceSheetReport from './BalanceSheetReport';
import CashFlowReport from './CashFlowReport';
import TrialBalanceReport from './TrialBalanceReport';
import GeneralLedgerReport from './GeneralLedgerReport';

const FinancialReportsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState<'profit-loss' | 'balance-sheet' | 'cash-flow' | 'trial-balance' | 'general-ledger'>('profit-loss');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [showComparison, setShowComparison] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const queryClient = useQueryClient();

  // Get current site ID - Fixed for rate limiting issue
  const currentSiteId = localStorage.getItem('activeSiteId') || null;

  // Don't make API calls if no site is selected
  const hasValidSiteId = Boolean(currentSiteId && currentSiteId.trim() !== '');

  // Debug: Log user and site information
  console.log('=== Financial Reports Debug ===');
  console.log('Current user:', user);
  console.log('User role:', user?.role);
  console.log('Current site ID:', currentSiteId);
  console.log('Has valid site ID:', hasValidSiteId);
  console.log('============================');

  // Initialize accounting system mutation
  const initializeAccountingMutation = useMutation(
    () => {
      console.log('Mutation function called');
      console.log('currentSiteId:', currentSiteId);
      
      if (!currentSiteId || currentSiteId.trim() === '') {
        console.log('No siteId found, throwing error');
        throw new Error('No site selected. Please select a site first.');
      }
      
      console.log('Calling accountingAPI.initializeAccounting with siteId:', currentSiteId);
      return accountingAPI.initializeAccounting(currentSiteId);
    },
    {
      onSuccess: (data) => {
        console.log('Accounting initialization success:', data);
        toast.success('Accounting system initialized successfully!');
        setShowSetupModal(false);
        // Refresh all financial reports queries
        queryClient.invalidateQueries(['profit-loss', 'balance-sheet', 'cash-flow']);
      },
      onError: (error: any) => {
        console.error('Full error object:', error);
        console.error('Error response:', error?.response);
        console.error('Error data:', error?.response?.data);
        console.error('Error status:', error?.response?.status);
        console.error('Error config:', error?.config);
        
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initialize accounting system';
        toast.error(errorMessage);
        console.error('Accounting initialization error:', error);
      }
    }
  );

  // Fetch summary data for dashboard
  const { data: profitLossData, isLoading: isLoadingPL } = useQuery(
    ['profit-loss', currentSiteId, dateRange],
    () => financialReportsAPI.getProfitAndLoss(currentSiteId!, dateRange),
    {
      enabled: activeReport === 'profit-loss' && hasValidSiteId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const { data: balanceSheetData, isLoading: isLoadingBS } = useQuery(
    ['balance-sheet', currentSiteId, dateRange.endDate],
    () => financialReportsAPI.getBalanceSheet(currentSiteId!, { asOfDate: dateRange.endDate }),
    {
      enabled: activeReport === 'balance-sheet' && hasValidSiteId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const { data: cashFlowData, isLoading: isLoadingCF } = useQuery(
    ['cash-flow', currentSiteId, dateRange],
    () => financialReportsAPI.getCashFlow(currentSiteId!, dateRange),
    {
      enabled: activeReport === 'cash-flow' && hasValidSiteId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const handleInitialize = async () => {
    console.log('handleInitialize called');
    console.log('currentSiteId:', currentSiteId);
    console.log('hasValidSiteId:', hasValidSiteId);
    console.log('user role:', user?.role);
    
    // Check if user has required role
    if (!user || !['admin', 'super_admin', 'manager'].includes(user.role)) {
      toast.error('You need admin or manager permissions to initialize the accounting system');
      return;
    }
    
    if (!hasValidSiteId) {
      toast.error('Please select a site first');
      return;
    }
    
    console.log('Calling initializeAccountingMutation.mutate()');
    initializeAccountingMutation.mutate();
  };

  const handleExport = (reportType: string) => {
    // This would implement PDF/Excel export functionality
    toast.success(`Exporting ${reportType} report...`);
  };

  const reportTabs = [
    { id: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp },
    { id: 'balance-sheet', label: 'Balance Sheet', icon: PieChart },
    { id: 'cash-flow', label: 'Cash Flow', icon: BarChart3 },
    { id: 'trial-balance', label: 'Trial Balance', icon: FileText },
    { id: 'general-ledger', label: 'General Ledger', icon: Eye }
  ];

  // Summary cards data
  const summaryCards = [
    {
      title: 'Total Revenue',
      value: profitLossData?.data?.revenue.total || 0,
      change: profitLossData?.data?.comparison ? 
        ((profitLossData.data.revenue.total - profitLossData.data.comparison.revenue) / profitLossData.data.comparison.revenue * 100) : 0,
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      title: 'Net Income',
      value: profitLossData?.data?.netIncome || 0,
      change: profitLossData?.data?.comparison ? 
        ((profitLossData.data.netIncome - profitLossData.data.comparison.netIncome) / Math.abs(profitLossData.data.comparison.netIncome) * 100) : 0,
      icon: DollarSign,
      color: profitLossData?.data?.netIncome && profitLossData.data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      title: 'Total Assets',
      value: balanceSheetData?.data?.totalAssets || 0,
      change: 0, // Would need previous period data
      icon: PieChart,
      color: 'text-blue-600'
    },
    {
      title: 'Cash Balance',
      value: cashFlowData?.data?.cashBalance.ending || 0,
      change: cashFlowData?.data?.netCashFlow || 0,
      icon: BarChart3,
      color: cashFlowData?.data?.netCashFlow && cashFlowData.data.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'
    }
  ];

  return (
    <div className="p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="h-6 w-px bg-gray-300"></div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-3">
            <button
              onClick={() => setShowSetupModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Settings className="w-4 h-4" />
              Setup
            </button>
            <button
              onClick={() => handleExport(activeReport)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Period:</span>
          </div>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showComparison}
              onChange={(e) => setShowComparison(e.target.checked)}
              className="rounded border-gray-300"
            />
            Compare with previous period
          </label>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(card.value)}</p>
                {card.change !== 0 && (
                  <div className={`flex items-center gap-1 mt-1 text-sm ${card.color}`}>
                    {card.change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span>{formatPercentage(Math.abs(card.change))}</span>
                  </div>
                )}
              </div>
              <card.icon className={`w-8 h-8 ${card.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Report Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {reportTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveReport(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm ${
                  activeReport === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Report Content */}
        <div className="p-6">
          {!hasValidSiteId ? (
            <div className="text-center py-12">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
                <Settings className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Site Selected</h3>
                <p className="text-yellow-700 mb-4">
                  Please select a site from the dashboard to view financial reports.
                </p>
                <Link
                  to="/admin"
                  className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Link>
              </div>
            </div>
          ) : (
            <>
              {activeReport === 'profit-loss' && (
                <ProfitLossReport 
                  data={profitLossData?.data} 
                  isLoading={isLoadingPL}
                  dateRange={dateRange}
                  showComparison={showComparison}
                />
              )}
              {activeReport === 'balance-sheet' && (
                <BalanceSheetReport 
                  data={balanceSheetData?.data} 
                  isLoading={isLoadingBS}
                  asOfDate={dateRange.endDate}
                />
              )}
              {activeReport === 'cash-flow' && (
                <CashFlowReport 
                  data={cashFlowData?.data} 
                  isLoading={isLoadingCF}
                  dateRange={dateRange}
                />
              )}
              {activeReport === 'trial-balance' && (
                <TrialBalanceReport 
                  siteId={currentSiteId!}
                  asOfDate={dateRange.endDate}
                />
              )}
              {activeReport === 'general-ledger' && (
                <GeneralLedgerReport 
                  siteId={currentSiteId!}
                  dateRange={dateRange}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Initialize Accounting System</h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                This will set up the chart of accounts and generate journal entries from your existing orders and expenses.
              </p>
              
              {!hasValidSiteId && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ No site selected. Please select a site from the dashboard first.
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">What this will do:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Create default chart of accounts</li>
                  <li>• Set up financial periods</li>
                  <li>• Generate journal entries from existing data</li>
                  <li>• Post all entries to the ledger</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowSetupModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleInitialize}
                disabled={initializeAccountingMutation.isLoading || !hasValidSiteId}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {initializeAccountingMutation.isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4" />
                    Initialize
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReportsDashboard;
